/* eslint-disable max-lines-per-function */
import { useEffect, useState } from 'react';

import type { ClientDocResponse } from '@/domain/models/client.js';
import type { UserModel } from '@/domain/models/user.js';
import type { ClientController } from '@/presentation/controllers/client-controller.js';

const isTempId = (id: string) => id.startsWith('temp-');
const makeTempId = () => `temp-${crypto.randomUUID()}`;

export function useClientDocuments(
    controller: ClientController,
    currentUser: UserModel | null,
    effectiveClientId: string | undefined,
    open: boolean,
    isEdit: boolean,
    clientId: string | undefined
) {
    const [documents, setDocuments] = useState<ClientDocResponse[]>([]);
    const [uploadingCategory, setUploadingCategory] = useState<string | null>(null);
    const [pendingDocumentFiles, setPendingDocumentFiles] = useState<Record<string, File>>({});

    useEffect(() => {
        if (!open) {
            return;
        }
        setDocuments([]);
        setPendingDocumentFiles({});
        if (isEdit && clientId) {
            controller.listDocuments(clientId).then((result) => {
                result.fold(
                    () => {},
                    (list) => setDocuments(list)
                );
            });
        }
    }, [open, isEdit, clientId, controller]);

    const uploadDocument = async (category: string, file: File): Promise<boolean> => {
        if (!effectiveClientId) {
            setDocuments((prev) => [
                ...prev.filter((d) => d.category !== category),
                {
                    id: makeTempId(),
                    clientId: '',
                    category,
                    fileName: file.name,
                    fileUrl: '',
                    fileSizeBytes: String(file.size),
                    mimeType: file.type || null,
                    uploadedAt: new Date().toISOString(),
                    uploadedBy: currentUser?.id ?? null,
                    uploadedByName: currentUser?.name ?? null
                }
            ]);
            setPendingDocumentFiles((prev) => ({ ...prev, [category]: file }));
            return true;
        }
        setUploadingCategory(category);
        try {
            const result = await controller.uploadDocument(effectiveClientId, { category, file });
            if (result.isRight()) {
                setDocuments((prev) => [
                    ...prev.filter((d) => d.category !== category),
                    { ...result.value, uploadedByName: result.value.uploadedByName ?? currentUser?.name ?? null }
                ]);
                return true;
            }
            return false;
        } finally {
            setUploadingCategory(null);
        }
    };

    const downloadDocument = async (doc: ClientDocResponse): Promise<boolean> => {
        if (!effectiveClientId || isTempId(doc.id)) {
            return false;
        }
        const result = await controller.getDocumentDownloadUrl(effectiveClientId, doc.id);
        if (result.isRight()) {
            window.open(result.value.url, '_blank', 'noopener,noreferrer');
            return true;
        }
        return false;
    };

    const deleteDocument = async (docId: string): Promise<boolean> => {
        if (isTempId(docId)) {
            const staged = documents.find((d) => d.id === docId);
            setDocuments((prev) => prev.filter((d) => d.id !== docId));
            if (staged) {
                setPendingDocumentFiles((prev) => {
                    const next = { ...prev };
                    delete next[staged.category];
                    return next;
                });
            }
            return true;
        }
        if (!effectiveClientId) {
            return false;
        }
        const result = await controller.deleteDocument(effectiveClientId, docId);
        if (result.isRight()) {
            setDocuments((prev) => prev.filter((d) => d.id !== docId));
            return true;
        }
        return false;
    };

    // Persists everything still staged locally (temp- ids) once the client itself exists.
    // Returns the categories that failed, so they stay staged for retry.
    const persistPending = async (targetClientId: string): Promise<string[]> => {
        const failures: string[] = [];
        const entries = Object.entries(pendingDocumentFiles);
        const uploaded: ClientDocResponse[] = [];

        for (const [category, file] of entries) {
            const result = await controller.uploadDocument(targetClientId, { category, file });
            if (result.isRight()) {
                uploaded.push({ ...result.value, uploadedByName: result.value.uploadedByName ?? currentUser?.name ?? null });
            } else {
                failures.push(category);
            }
        }

        const failedCategories = new Set(failures);
        setDocuments((prev) => {
            const next = prev.filter((d) => !isTempId(d.id) || failedCategories.has(d.category));
            for (const doc of uploaded) {
                next.push(doc);
            }
            return next;
        });
        setPendingDocumentFiles(() => {
            const next: Record<string, File> = {};
            for (const [category, file] of entries) {
                if (failedCategories.has(category)) {
                    next[category] = file;
                }
            }
            return next;
        });

        return failures;
    };

    return { documents, uploadingCategory, uploadDocument, downloadDocument, deleteDocument, persistPending };
}
